import React, { useState } from "react";
import { Checkbox, Modal, Text, Stack, Group, Badge } from "@mantine/core";
import { IconShieldCheck, IconShieldOff } from "@tabler/icons-react";
import { useGlobal } from "../context/global-context";
import { BRAND_COPY, isSiteConsent, isoNow, consentIndicatorLabel } from "../utils/consent";

// Required checkbox shown above a flow's submit button when the demo setting
// "Notice and consent" is "Handled by this site". The timestamp of the tick is
// what goes into the create-journey attestation, so onChange receives an ISO
// string (checked) or null (unchecked).
export function ConsentCheckbox({ brand, acceptedAt, onChange, disabled, dark = false }) {
  const { wineStoreSettings } = useGlobal();
  const [doc, setDoc] = useState(null); // "terms" | "privacy" | null
  if (!isSiteConsent(wineStoreSettings)) return null;

  const copy = BRAND_COPY[brand] || BRAND_COPY.wine;
  const linkStyle = {
    color: dark ? "#00E5C7" : "#1c1c1e",
    textDecoration: "underline",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
  };

  return (
    <>
      <Checkbox
        checked={!!acceptedAt}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.checked ? isoNow() : null)}
        color={dark ? "teal" : "dark"}
        styles={{ label: { color: dark ? "#d6dcdb" : undefined, fontSize: 14 } }}
        label={
          <span>
            I have read and agree to the{" "}
            <button type="button" style={linkStyle} onClick={() => setDoc("terms")}>
              Terms of Service
            </button>{" "}
            and{" "}
            <button type="button" style={linkStyle} onClick={() => setDoc("privacy")}>
              Privacy Notice
            </button>
          </span>
        }
      />

      <Modal
        opened={!!doc}
        onClose={() => setDoc(null)}
        title={doc === "terms" ? `${copy.name} Terms of Service` : `${copy.name} Privacy Notice`}
        centered
        zIndex={10000}
      >
        <Stack gap="sm">
          <Badge color="gray" variant="light" w="fit-content">Placeholder copy for demo purposes</Badge>
          {(doc === "terms" ? copy.terms : copy.privacy).map((p, i) => (
            <Text key={i} size="sm">{p}</Text>
          ))}
        </Stack>
      </Modal>
    </>
  );
}

// Small state indicator for the prospect: shows whether VX's own
// notice-and-consent screen will appear during verification.
export function ConsentIndicator({ dark = false, align = "center" }) {
  const { wineStoreSettings } = useGlobal();
  const site = isSiteConsent(wineStoreSettings);
  const color = dark ? (site ? "#00E5C7" : "#9ba5a3") : site ? "#0d9488" : "#6b7574";
  return (
    <Group gap={6} justify={align} wrap="nowrap" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
      {site ? <IconShieldOff size={13} color={color} /> : <IconShieldCheck size={13} color={color} />}
      <Text size="xs" style={{ color, letterSpacing: "0.04em" }}>
        {consentIndicatorLabel(wineStoreSettings)}
      </Text>
    </Group>
  );
}
